import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listingsApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { ArrowLeft, Upload, X, Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';

const listingTypes = [
  { value: 'stall', label: 'Stall' },
  { value: 'pasture', label: 'Pasture' },
  { value: 'layover', label: 'Layover' },
  { value: 'full_boarding', label: 'Full Boarding' },
];

const speciesOptions = ['horses', 'cattle', 'sheep', 'goats', 'donkeys', 'llamas'];

const amenitiesOptions = [
  'Covered arena',
  'Round pen',
  'Trails access',
  'Wash rack',
  'Tack room',
  'Feed storage',
  'Hot water',
  'Lighting',
  'Security cameras',
  '24/7 access',
  'Caretaker on-site',
  'Veterinary access',
];

export default function CreateListing() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    title: '',
    description: '',
    listing_type: 'stall',
    location: {
      address: '',
      city: '',
      state: '',
      zip_code: '',
      country: 'USA',
    },
    capacity: 1,
    species_allowed: ['horses'],
    amenities: [],
    rules: {
      vaccination: false,
      quarantine: false,
      owner_access: true,
    },
    pricing: {
      nightly: '',
      weekly: '',
      monthly: '',
    },
    photos: [],
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.title || !form.description || !form.location.city || !form.location.state || !form.pricing.nightly) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...form,
        pricing: {
          nightly: parseFloat(form.pricing.nightly),
          weekly: form.pricing.weekly ? parseFloat(form.pricing.weekly) : null,
          monthly: form.pricing.monthly ? parseFloat(form.pricing.monthly) : null,
        },
      };
      await listingsApi.create(payload);
      toast.success('Listing created! It will be reviewed by our team.');
      navigate('/host/listings');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create listing');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setForm((prev) => ({
          ...prev,
          photos: [...prev.photos, reader.result],
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (index) => {
    setForm((prev) => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index),
    }));
  };

  const toggleSpecies = (species) => {
    setForm((prev) => ({
      ...prev,
      species_allowed: prev.species_allowed.includes(species)
        ? prev.species_allowed.filter((s) => s !== species)
        : [...prev.species_allowed, species],
    }));
  };

  const toggleAmenity = (amenity) => {
    setForm((prev) => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter((a) => a !== amenity)
        : [...prev.amenities, amenity],
    }));
  };

  return (
    <div className="min-h-screen bg-farmhouse-charcoal pt-8 pb-16" data-testid="create-listing-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-farmhouse-muted hover:text-farmhouse-cream mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        <h1 className="font-serif text-4xl font-semibold text-farmhouse-cream mb-8">
          Create New Listing
        </h1>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Info */}
          <section className="bg-farmhouse-paper border border-farmhouse-subtle rounded-md p-6">
            <h2 className="font-serif text-xl font-semibold text-farmhouse-cream mb-4">
              Basic Information
            </h2>
            
            <div className="space-y-4">
              <div>
                <Label className="text-farmhouse-cream">Title *</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g., Peaceful Barn with 5 Stalls"
                  className="form-input mt-1"
                  required
                  data-testid="listing-title"
                />
              </div>

              <div>
                <Label className="text-farmhouse-cream">Description *</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Describe your space, facilities, and what makes it special..."
                  className="form-input mt-1 min-h-[120px]"
                  required
                  data-testid="listing-description"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-farmhouse-cream">Type *</Label>
                  <Select
                    value={form.listing_type}
                    onValueChange={(value) => setForm({ ...form, listing_type: value })}
                  >
                    <SelectTrigger className="form-input mt-1" data-testid="listing-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-farmhouse-paper border-farmhouse-subtle">
                      {listingTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value} className="text-farmhouse-cream hover:bg-farmhouse-subtle">
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-farmhouse-cream">Capacity *</Label>
                  <Input
                    type="number"
                    min={1}
                    value={form.capacity}
                    onChange={(e) => setForm({ ...form, capacity: parseInt(e.target.value) })}
                    className="form-input mt-1"
                    required
                    data-testid="listing-capacity"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Location */}
          <section className="bg-farmhouse-paper border border-farmhouse-subtle rounded-md p-6">
            <h2 className="font-serif text-xl font-semibold text-farmhouse-cream mb-4">
              Location
            </h2>
            
            <div className="space-y-4">
              <div>
                <Label className="text-farmhouse-cream">Address</Label>
                <Input
                  value={form.location.address}
                  onChange={(e) => setForm({ ...form, location: { ...form.location, address: e.target.value }})}
                  placeholder="Street address"
                  className="form-input mt-1"
                  data-testid="listing-address"
                />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="col-span-2">
                  <Label className="text-farmhouse-cream">City *</Label>
                  <Input
                    value={form.location.city}
                    onChange={(e) => setForm({ ...form, location: { ...form.location, city: e.target.value }})}
                    placeholder="City"
                    className="form-input mt-1"
                    required
                    data-testid="listing-city"
                  />
                </div>
                <div>
                  <Label className="text-farmhouse-cream">State *</Label>
                  <Input
                    value={form.location.state}
                    onChange={(e) => setForm({ ...form, location: { ...form.location, state: e.target.value }})}
                    placeholder="State"
                    className="form-input mt-1"
                    required
                    data-testid="listing-state"
                  />
                </div>
                <div>
                  <Label className="text-farmhouse-cream">ZIP</Label>
                  <Input
                    value={form.location.zip_code}
                    onChange={(e) => setForm({ ...form, location: { ...form.location, zip_code: e.target.value }})}
                    placeholder="ZIP"
                    className="form-input mt-1"
                    data-testid="listing-zip"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Species & Amenities */}
          <section className="bg-farmhouse-paper border border-farmhouse-subtle rounded-md p-6">
            <h2 className="font-serif text-xl font-semibold text-farmhouse-cream mb-4">
              Species & Amenities
            </h2>
            
            <div className="space-y-6">
              <div>
                <Label className="text-farmhouse-cream mb-3 block">Species Allowed</Label>
                <div className="flex flex-wrap gap-2">
                  {speciesOptions.map((species) => (
                    <button
                      key={species}
                      type="button"
                      onClick={() => toggleSpecies(species)}
                      className={`px-4 py-2 rounded-sm border transition-colors capitalize ${
                        form.species_allowed.includes(species)
                          ? 'border-farmhouse-saddle bg-farmhouse-saddle/10 text-farmhouse-saddle'
                          : 'border-farmhouse-subtle text-farmhouse-muted hover:border-farmhouse-stone'
                      }`}
                    >
                      {species}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-farmhouse-cream mb-3 block">Amenities</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {amenitiesOptions.map((amenity) => (
                    <label
                      key={amenity}
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <Checkbox
                        checked={form.amenities.includes(amenity)}
                        onCheckedChange={() => toggleAmenity(amenity)}
                        className="border-farmhouse-subtle data-[state=checked]:bg-farmhouse-saddle"
                      />
                      <span className="text-farmhouse-cream text-sm">{amenity}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Rules */}
          <section className="bg-farmhouse-paper border border-farmhouse-subtle rounded-md p-6">
            <h2 className="font-serif text-xl font-semibold text-farmhouse-cream mb-4">
              Rules & Requirements
            </h2>
            
            <div className="space-y-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox
                  checked={form.rules.vaccination}
                  onCheckedChange={(checked) => setForm({ ...form, rules: { ...form.rules, vaccination: checked }})}
                  className="border-farmhouse-subtle data-[state=checked]:bg-farmhouse-saddle"
                />
                <span className="text-farmhouse-cream">Vaccination records required</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox
                  checked={form.rules.quarantine}
                  onCheckedChange={(checked) => setForm({ ...form, rules: { ...form.rules, quarantine: checked }})}
                  className="border-farmhouse-subtle data-[state=checked]:bg-farmhouse-saddle"
                />
                <span className="text-farmhouse-cream">Quarantine period required for new arrivals</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox
                  checked={form.rules.owner_access}
                  onCheckedChange={(checked) => setForm({ ...form, rules: { ...form.rules, owner_access: checked }})}
                  className="border-farmhouse-subtle data-[state=checked]:bg-farmhouse-saddle"
                />
                <span className="text-farmhouse-cream">Owner can visit anytime</span>
              </label>
            </div>
          </section>

          {/* Pricing */}
          <section className="bg-farmhouse-paper border border-farmhouse-subtle rounded-md p-6">
            <h2 className="font-serif text-xl font-semibold text-farmhouse-cream mb-4">
              Pricing
            </h2>
            
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-farmhouse-cream">Nightly Rate *</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-farmhouse-muted">$</span>
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    value={form.pricing.nightly}
                    onChange={(e) => setForm({ ...form, pricing: { ...form.pricing, nightly: e.target.value }})}
                    className="form-input pl-8"
                    required
                    data-testid="pricing-nightly"
                  />
                </div>
              </div>
              <div>
                <Label className="text-farmhouse-cream">Weekly Rate</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-farmhouse-muted">$</span>
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    value={form.pricing.weekly}
                    onChange={(e) => setForm({ ...form, pricing: { ...form.pricing, weekly: e.target.value }})}
                    className="form-input pl-8"
                    data-testid="pricing-weekly"
                  />
                </div>
              </div>
              <div>
                <Label className="text-farmhouse-cream">Monthly Rate</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-farmhouse-muted">$</span>
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    value={form.pricing.monthly}
                    onChange={(e) => setForm({ ...form, pricing: { ...form.pricing, monthly: e.target.value }})}
                    className="form-input pl-8"
                    data-testid="pricing-monthly"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Photos */}
          <section className="bg-farmhouse-paper border border-farmhouse-subtle rounded-md p-6">
            <h2 className="font-serif text-xl font-semibold text-farmhouse-cream mb-4">
              Photos
            </h2>
            
            <div className="space-y-4">
              <div className="grid grid-cols-3 md:grid-cols-4 gap-4">
                {form.photos.map((photo, index) => (
                  <div key={index} className="relative aspect-square rounded-md overflow-hidden">
                    <img src={photo} alt={`Photo ${index + 1}`} className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removePhoto(index)}
                      className="absolute top-2 right-2 p-1 bg-farmhouse-charcoal/80 rounded-full text-white hover:bg-farmhouse-charcoal"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                
                <label className="aspect-square rounded-md border-2 border-dashed border-farmhouse-subtle hover:border-farmhouse-saddle transition-colors cursor-pointer flex flex-col items-center justify-center gap-2">
                  <Upload className="w-8 h-8 text-farmhouse-muted" />
                  <span className="text-sm text-farmhouse-muted">Add Photo</span>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handlePhotoUpload}
                    className="hidden"
                    data-testid="photo-upload"
                  />
                </label>
              </div>
              <p className="text-farmhouse-muted text-sm">
                Add at least 3 photos to help guests understand your space
              </p>
            </div>
          </section>

          {/* Submit */}
          <div className="flex gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(-1)}
              className="flex-1 btn-secondary"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 btn-primary"
              disabled={loading}
              data-testid="submit-listing"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Create Listing'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
